import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { Card } from "../molecules/Card";
import { Button } from "../atoms/Button";
import { formatCurrency } from "../../utils/currency";
import { formatTimeAgo } from "../../utils/date";
import type { AlertWithContext } from "../../types/models";

type CashIndicator = "sufficient" | "insufficient" | "none";

function getCashIndicator(alert: AlertWithContext): CashIndicator {
  const cash = alert.current_available_cash;
  const cost = alert.estimated_reorder_cost;
  if (cash <= 0) return "none";
  if (cash >= cost) return "sufficient";
  return "insufficient";
}

function StatusBadge({ alert }: { alert: AlertWithContext }) {
  const { t } = useTranslation("alerts");
  const isOut = alert.alert_type === "out_of_stock";
  const label = isOut ? t("card_status_out") : t("card_status_low");
  const bg = isOut
    ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
    : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${bg}`}
      aria-label={t("card_status_aria", { label })}
    >
      {isOut ? "🔴" : "🟡"} {label}
    </span>
  );
}

function CashIndicatorLine({ alert }: { alert: AlertWithContext }) {
  const { t } = useTranslation("alerts");
  const ind = getCashIndicator(alert);
  const cash = alert.current_available_cash;
  const cost = alert.estimated_reorder_cost;
  const needUnits = Math.max(0, alert.reorder_threshold - alert.current_quantity);
  const needUnitsDisplay = alert.alert_type === "out_of_stock"
    ? alert.reorder_threshold
    : needUnits;

  if (ind === "sufficient") {
    return (
      <p className="text-sm text-emerald-700 dark:text-emerald-400">
        {t("card_cash_sufficient", { units: needUnitsDisplay, cost: formatCurrency(cost), cash: formatCurrency(cash) })}
      </p>
    );
  }
  if (ind === "insufficient") {
    return (
      <p className="text-sm text-amber-700 dark:text-amber-400">
        {t("card_cash_insufficient", { units: needUnitsDisplay, cost: formatCurrency(cost), cash: formatCurrency(cash) })}
      </p>
    );
  }
  return (
    <p className="text-sm text-red-700 dark:text-red-400">
      {t("card_cash_none")}
    </p>
  );
}

export interface AlertCardProps {
  alert: AlertWithContext;
  onViewItem?: (inventoryItemId: string) => void;
  onCreatePurchase?: (alert: AlertWithContext) => void;
  onDismiss?: (alertId: string) => void;
  onAcknowledge?: (alertId: string) => void;
}

/**
 * Single reorder alert card with cash context (Story 6.2).
 * Compact view on mobile; tap to expand full details.
 */
export function AlertCard({
  alert,
  onViewItem,
  onCreatePurchase,
  onDismiss,
  onAcknowledge,
}: AlertCardProps) {
  const { t } = useTranslation("alerts");
  const [expanded, setExpanded] = useState(false);
  const isAcknowledged = alert.status === "acknowledged";

  const compact = (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-neutral-900 dark:text-neutral-100 truncate">
          {alert.item_name}
        </span>
        <StatusBadge alert={alert} />
      </div>
      <p className="text-sm text-neutral-600 dark:text-neutral-400">
        {t("card_qty_left", { qty: alert.current_quantity, threshold: alert.reorder_threshold })}
      </p>
      <p className="text-sm text-neutral-500 dark:text-neutral-400">
        {formatTimeAgo(alert.created_at)}
      </p>
      <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
        {t("card_cash_available", { cash: formatCurrency(alert.current_available_cash) })}
      </p>
      {/* Mobile: always show primary actions so users can act without expanding */}
      <div className="mt-3 flex flex-wrap gap-2">
        {onViewItem && (
          <Button variant="secondary" size="sm" onClick={() => onViewItem(alert.inventory_item_id)} className="min-h-[44px] min-w-[44px]">
            {t("card_btn_view_item")}
          </Button>
        )}
        {onCreatePurchase && (
          <Button variant="primary" size="sm" onClick={() => onCreatePurchase(alert)} className="min-h-[44px]">
            {t("card_btn_create_purchase")}
          </Button>
        )}
        {onDismiss && (
          <Button variant="secondary" size="sm" onClick={() => onDismiss(alert.id)} className="min-h-[44px] min-w-[44px]">
            {t("card_btn_dismiss")}
          </Button>
        )}
        {onAcknowledge && (
          <Button variant="secondary" size="sm" onClick={() => onAcknowledge(alert.id)} className="min-h-[44px] min-w-[44px]">
            {t("card_btn_acknowledge")}
          </Button>
        )}
      </div>
    </>
  );

  const fullDetails = (
    <>
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="font-medium text-neutral-900 dark:text-neutral-100">
          {alert.item_name}
          {alert.item_sku ? ` (${alert.item_sku})` : ""}
        </span>
        <StatusBadge alert={alert} />
      </div>
      <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-1">
        {t("card_qty_detail", { qty: alert.current_quantity, threshold: alert.reorder_threshold })}
      </p>
      <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-1">
        {formatTimeAgo(alert.created_at)}
      </p>
      <p className="text-sm text-neutral-700 dark:text-neutral-300 mb-1">
        {t("card_reorder_cost", { cost: formatCurrency(alert.estimated_reorder_cost) })}
      </p>
      <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-2">
        {t("card_cash_for_purchasing", { cash: formatCurrency(alert.current_available_cash) })}
      </p>
      <CashIndicatorLine alert={alert} />

      {/* Quick actions */}
      <div className="mt-4 flex flex-wrap gap-2">
        {onViewItem && (
          <Button variant="secondary" size="sm" onClick={() => onViewItem(alert.inventory_item_id)}>
            {t("card_btn_view_item")}
          </Button>
        )}
        {onCreatePurchase && (
          <Button variant="primary" size="sm" onClick={() => onCreatePurchase(alert)}>
            {t("card_btn_create_purchase")}
          </Button>
        )}
        {onDismiss && (
          <Button variant="secondary" size="sm" onClick={() => onDismiss(alert.id)}>
            {t("card_btn_dismiss")}
          </Button>
        )}
        {onAcknowledge && (
          <Button variant="secondary" size="sm" onClick={() => onAcknowledge(alert.id)}>
            {t("card_btn_acknowledge")}
          </Button>
        )}
      </div>
    </>
  );

  const isCompactView = !expanded;

  return (
    <Card
      padding="md"
      className={`border ${isAcknowledged ? "border-neutral-200 dark:border-neutral-600 opacity-90 bg-neutral-50/50 dark:bg-neutral-800/50" : "border-neutral-200 dark:border-neutral-700"}`}
    >
      <div className="md:block flex flex-col">
        {/* Mobile: compact by default with actions visible; tap to expand for full details */}
        <div className="md:hidden">
          {isCompactView ? compact : fullDetails}
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="mt-2 flex items-center justify-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:underline min-h-[44px] w-full rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700/50"
            aria-expanded={expanded}
          >
            {expanded ? (
              <>
                <ChevronUpIcon className="w-4 h-4" /> {t("card_toggle_less")}
              </>
            ) : (
              <>
                <ChevronDownIcon className="w-4 h-4" /> {t("card_toggle_more")}
              </>
            )}
          </button>
        </div>
        {/* Desktop: always full */}
        <div className="hidden md:block">{fullDetails}</div>
      </div>
    </Card>
  );
}
